import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Breadcrumbs, type Crumb } from "@/components/Breadcrumbs";
import {
  getArticleByPath,
  getCuratedNextRead,
  getRelatedArticles,
  getTopicLandingByCategory,
  type ArticleCategory,
} from "@/lib/learningHub";
import { MoneyPageLinks } from "@/components/MoneyPageLinks";
import { ArticleScrollProgress } from "@/components/ArticleScrollProgress";
import { ArticleTableOfContents } from "@/components/ArticleTableOfContents";
import chrisPhoto from "@/assets/chris-heidlebaugh-colorado-web-designer.png";

export type ArticleFaq = { q: string; a: string };

type Props = {
  eyebrow: string;
  title: string;
  intro: string;
  /** Estimated read time. Falls back to learningHub registry when omitted. */
  read?: string;
  children: ReactNode;
  /** Back link target (defaults to /honest-answers for legacy compat) */
  backTo?: string;
  backLabel?: string;
  breadcrumbs?: Crumb[];
  /** Canonical path of this article (e.g. "/learn/local-seo-guide") for JSON-LD */
  path?: string;
  /** ISO date string for datePublished in JSON-LD */
  datePublished?: string;
  /** ISO date string for dateModified in JSON-LD */
  dateModified?: string;
  /** Optional FAQ pairs — emits FAQPage structured data when provided */
  faqs?: ArticleFaq[];
};

import { SCHEMA_ORG_ID, SCHEMA_PERSON_ID } from "@/lib/entitySchema";

const SITE_URL = "https://coloradowebimpressions.com";

function formatLongDate(iso: string): string {
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export function ArticleLayout({
  eyebrow,
  title,
  intro,
  read,
  children,
  backTo = "/learn",
  backLabel = "Back to Learn (Blog)",
  breadcrumbs,
  path,
  datePublished,
  dateModified,
  faqs,
}: Props) {
  // Pull canonical metadata from the central learningHub registry whenever a
  // path is provided. Per-article props still win (backward compatible), but
  // by default every article gets the same source-of-truth dates + read time
  // so the byline, JSON-LD, and sitemap stay in sync.
  const articleMeta = path ? getArticleByPath(path) : undefined;
  const resolvedRead = read ?? articleMeta?.read ?? "";
  const resolvedPublished =
    datePublished ?? articleMeta?.datePublished ?? "2025-01-15";
  const resolvedModified =
    dateModified ?? articleMeta?.dateUpdated ?? resolvedPublished;
  const url = path ? `${SITE_URL}${path}` : undefined;

  // Best-effort word count from the configured "X min read" string (~225 wpm
  // is the Medium/Nielsen norm). Used for `wordCount` in JSON-LD only.
  const minutesMatch = resolvedRead.match(/(\d+)/);
  const wordCount = minutesMatch ? parseInt(minutesMatch[1], 10) * 225 : undefined;
  const heroImage = articleMeta?.image
    ? (articleMeta.image.startsWith("http")
        ? articleMeta.image
        : `${SITE_URL}${articleMeta.image.startsWith("/") ? "" : "/"}${articleMeta.image}`)
    : `${SITE_URL}/og-default.jpg`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: intro,
    // Inline the author/publisher properties Google's Article rich-result spec
    // requires (name, url, logo) AND keep the @id reference so the per-article
    // node stitches into the sitewide Person/Organization entities emitted from
    // __root.tsx. Both forms together = canonical entity + rich-result eligible.
    author: {
      "@type": "Person",
      "@id": SCHEMA_PERSON_ID,
      name: "Chris Heidlebaugh",
      url: `${SITE_URL}/meet-chris`,
      jobTitle: "Founder & Lead Strategist",
      worksFor: { "@id": SCHEMA_ORG_ID },
      image: `${SITE_URL}/chris-heidlebaugh.png`,
      sameAs: [
        "https://chrisheidlebaugh.com",
        "https://digitalmarketingcoach.com",
        "https://www.linkedin.com/in/chrisheidlebaugh",
      ],
    },
    publisher: {
      "@type": "Organization",
      "@id": SCHEMA_ORG_ID,
      name: "Colorado Web Impressions",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
        width: 512,
        height: 512,
      },
    },
    image: [heroImage],
    datePublished: resolvedPublished,
    dateModified: resolvedModified,
    inLanguage: "en-US",
    articleSection: eyebrow,
    keywords: [eyebrow, "Colorado", "small business", "Colorado Springs"].join(", "),
    ...(wordCount ? { wordCount, timeRequired: `PT${minutesMatch![1]}M` } : {}),
    ...(url ? { mainEntityOfPage: { "@type": "WebPage", "@id": url }, url } : {}),
    isAccessibleForFree: true,
  };

  const faqSchema =
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  // BreadcrumbList JSON-LD mirrors the visible trail. Only emitted when
  // explicit breadcrumbs are provided (legacy "back link" mode skips this).
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.label,
            ...(c.to ? { item: `${SITE_URL}${c.to}` } : {}),
          })),
        }
      : null;
  return (
    <SiteLayout>
      {/* Article + FAQ schemas only — Organization & Person entities are emitted sitewide via __root.tsx. */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}
      <ArticleScrollProgress />
      <article key={path ?? title} className="bg-background animate-article-in">
        {/* Header */}
        <header className="bg-gradient-dark py-20 text-brand-cream sm:py-24 lg:py-28">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <Breadcrumbs items={breadcrumbs} />
            ) : (
              <Link
                to={backTo}
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand-cream/85 transition-smooth hover:text-brand-gold"
              >
                <ArrowLeft className="h-4 w-4" />
                {backLabel}
              </Link>
            )}
            {(() => {
              const topicLanding = getTopicLandingByCategory(eyebrow as ArticleCategory);
              if (!topicLanding) return null;
              return (
                <Link
                  to="/learn/topic/$topicSlug"
                  params={{ topicSlug: topicLanding.slug }}
                  className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold transition-smooth hover:border-brand-gold hover:bg-brand-gold/20"
                  aria-label={`See more ${eyebrow} articles in the Learn (Blog)`}
                >
                  <span aria-hidden="true">#</span>
                  {eyebrow}
                </Link>
              );
            })()}
            <h1 className="mt-5 text-4xl font-black leading-[1.1] text-white sm:text-5xl">
              {title}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-brand-cream/85">{intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-brand-cream/85">
              <span className="font-semibold text-brand-gold">
                By{" "}
                <Link to="/meet-chris" className="underline-offset-2 hover:underline">
                  Chris Heidlebaugh
                </Link>
                , Owner
              </span>
              <span aria-hidden="true">·</span>
              <span>{resolvedRead}</span>
              <span aria-hidden="true">·</span>
              <span>
                Published{" "}
                <time dateTime={resolvedPublished}>{formatLongDate(resolvedPublished)}</time>
              </span>
              {resolvedModified && resolvedModified !== resolvedPublished && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>
                    Updated{" "}
                    <time dateTime={resolvedModified}>{formatLongDate(resolvedModified)}</time>
                  </span>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20 lg:px-8 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-12 xl:grid-cols-[minmax(0,1fr)_20rem] xl:gap-16">
            {/* Main column */}
            <div className="min-w-0">
              <div className="prose-article">{children}</div>

              {/* Kyle Roof-style internal linking — keyword-rich anchors to city × service money pages */}
              <MoneyPageLinks category={eyebrow as ArticleCategory} />

              {/* Author bio box — moved to the bottom for blog-style reading flow */}
              <aside
                aria-label="About the author"
                className="mt-12 rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6"
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <img
                    src={chrisPhoto}
                    alt="Chris Heidlebaugh, Founder of Colorado Web Impressions — Colorado Springs web designer, SEO expert, and digital marketing coach"
                    width={96}
                    height={96}
                    loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-brand-gold shadow-gold sm:h-20 sm:w-20"
                    data-geo-lat="38.9359976"
                    data-geo-lng="-104.7653118"
                    data-geo-city="Colorado Springs"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <Link
                        to="/meet-chris"
                        className="text-base font-extrabold text-foreground transition-smooth hover:text-brand-gold-deep sm:text-lg"
                      >
                        Chris Heidlebaugh
                      </Link>
                      <span className="text-sm font-semibold text-brand-gold-deep">
                        Digital Marketing Coach · Founder, Colorado Web Impressions
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground/80 sm:text-[0.95rem]">
                      Chris is a best-selling author, entrepreneur, and industry leader with
                      more than 25 years of experience helping business owners and marketing
                      teams take control of their online presence. Through{" "}
                      <a
                        href="https://chrisheidlebaugh.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-brand-gold-deep underline underline-offset-2 hover:text-foreground"
                      >
                        ChrisHeidlebaugh.com
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://digitalmarketingcoach.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-brand-gold-deep underline underline-offset-2 hover:text-foreground"
                      >
                        DigitalMarketingCoach.com
                      </a>
                      , he provides training, courses, and hands-on coaching for companies
                      who want to confidently manage their own marketing.
                    </p>
                  </div>
                </div>

                {/* Credentials */}
                <ul className="mt-4 flex flex-wrap gap-1.5 border-t border-border pt-4">
                  {[
                    "Best-selling author",
                    "25+ years in digital marketing",
                    "Founder · Colorado Web Impressions",
                    "Digital Marketing Coach",
                    "Local SEO & Google Business Profile specialist",
                    "In-house · Colorado Springs",
                  ].map((cred) => (
                    <li
                      key={cred}
                      className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-semibold text-foreground/80"
                    >
                      {cred}
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <Link
                    to="/meet-chris"
                    className="font-bold text-brand-gold-deep underline underline-offset-2 hover:text-foreground"
                  >
                    Read full bio →
                  </Link>
                  <Link
                    to="/editorial-policy"
                    className="font-bold text-brand-gold-deep underline underline-offset-2 hover:text-foreground"
                  >
                    Editorial &amp; corrections policy →
                  </Link>
                  <span>
                    Last reviewed{" "}
                    <time dateTime={resolvedModified}>{formatLongDate(resolvedModified)}</time> by Chris
                  </span>
                </div>
              </aside>

              {/* Footer CTA */}
              <div className="mt-16 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-7 sm:p-9">
                <h3 className="text-2xl font-extrabold text-foreground">
                  Have questions this article didn&apos;t answer?
                </h3>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">
                  Book a no-pressure 30-minute call with Chris, the owner. If we&rsquo;re not a
                  fit, he&rsquo;ll tell you who is.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/contact"
                    className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-3.5 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                  >
                    Get a proposal
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                  <a
                    href="tel:+17194193935"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-7 py-3.5 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:bg-muted"
                   aria-label="Call (719) 419-3935">
                    <Phone className="h-4 w-4" />
                    (719) 419-3935
                  </a>
                </div>
              </div>

              {/* Cross-links */}
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                <Link
                  to="/learn"
                  className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 shadow-card transition-smooth hover:border-brand-gold/50"
                >
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                      More articles
                    </div>
                    <div className="mt-1 font-bold text-foreground">Back to Learn (Blog)</div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/are-we-a-fit"
                  className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 shadow-card transition-smooth hover:border-brand-gold/50"
                >
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                      2-Min Quiz
                    </div>
                    <div className="mt-1 font-bold text-foreground">Are we a fit?</div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Sidebar — Sticky TOC + Related articles */}
            <aside
              aria-label="Article navigation"
              className="space-y-6 lg:sticky lg:top-28 lg:self-start"
            >
              <ArticleTableOfContents />
              {path && <RelatedArticlesSidebar currentPath={path} />}
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}

function RelatedArticlesSidebar({ currentPath }: { currentPath: string }) {
  // Editorial pin (the curated next reading step) shown as a distinct CTA card.
  const nextRead = getCuratedNextRead(currentPath);

  // Pull a wider pool of algorithmic picks, then drop the curated next-read
  // so it doesn't appear twice. We aim for 4 algorithmic picks below the pin.
  const pool = getRelatedArticles(currentPath, 6).filter(
    (a) => !nextRead || a.slug !== nextRead.slug
  );
  const related = pool.slice(0, 4);

  if (!nextRead && related.length === 0) return null;

  // Unique topics across the visible set for the quick-jump filter chips
  const visible = [...(nextRead ? [nextRead] : []), ...related];
  const relatedTopics = Array.from(new Set(visible.map((a) => a.category)));

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card sm:p-6">
      {/* Curated "Next up" pin */}
      {nextRead && (
        <div className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
              ▶ Next up
            </span>
          </div>
          <Link
            to={nextRead.slug}
            className="group block rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-4 transition-smooth hover:border-brand-gold hover:bg-brand-gold/10"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-gold/20 text-brand-gold-deep">
                <nextRead.icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-[0.7rem] font-bold uppercase tracking-wider text-brand-gold-deep">
                  {nextRead.category}
                </div>
                <div className="mt-0.5 text-sm font-bold leading-snug text-foreground group-hover:text-brand-gold-deep">
                  {nextRead.title}
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground/60">
                  <span>{nextRead.read}</span>
                  <span aria-hidden="true">·</span>
                  <span className="font-semibold text-brand-gold-deep">
                    Continue reading →
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {related.length > 0 && (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
              Related articles
            </h2>
          </div>
          <ul className="space-y-1">
            {related.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.slug}>
                  <Link
                    to={a.slug}
                    className="group block rounded-lg p-3 transition-smooth hover:bg-muted"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-gold/10 text-brand-gold-deep">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[0.7rem] font-bold uppercase tracking-wider text-brand-gold-deep">
                          {a.category}
                        </div>
                        <div className="mt-0.5 text-sm font-bold leading-snug text-foreground group-hover:text-brand-gold-deep">
                          {a.title}
                        </div>
                        <div className="mt-1 text-xs text-foreground/60">{a.read}</div>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Clickable topic filters */}
      <div className="mt-5 border-t border-border pt-4">
        <div className="mb-2 text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
          Browse by topic
        </div>
        <div className="flex flex-wrap gap-1.5">
          {relatedTopics.map((topic) => {
            const landing = getTopicLandingByCategory(topic);
            if (!landing) return null;
            return (
              <Link
                key={topic}
                to="/learn/topic/$topicSlug"
                params={{ topicSlug: landing.slug }}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-smooth hover:border-brand-gold/60 hover:bg-brand-gold/10 hover:text-brand-gold-deep"
              >
                <span aria-hidden="true">#</span>
                {topic}
              </Link>
            );
          })}
        </div>
      </div>

      <Link
        to="/learn"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-gold-deep transition-smooth hover:text-foreground"
      >
        Browse all articles
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
